/**
 * Client portal + public proposal pages load branding images while signed in as
 * anonymous Firebase users. Media Library assets live under `media-library/`,
 * which Storage rules restrict to internal CRM — those URLs 403 for clients.
 * This helper copies such files to `portal-branding/` (see storage.rules).
 */

import { ref, getBytes, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

export function firebaseStorageObjectPathFromDownloadUrl(url: string): string | null {
  const trimmed = (url || '').trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed);
    // Default API: .../v0/b/BUCKET/o/ENCODED_PATH  (also used by many *.firebasestorage.app URLs)
    const m = u.pathname.match(/\/o\/([^?]+)/);
    if (!m?.[1]) return null;
    const encoded = m[1];
    try {
      return decodeURIComponent(encoded.replace(/\+/g, '%20'));
    } catch {
      return encoded;
    }
  } catch {
    return null;
  }
}

function contentTypeForFileName(name: string): string {
  const n = name.toLowerCase();
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  if (n.endsWith('.webp')) return 'image/webp';
  if (n.endsWith('.gif')) return 'image/gif';
  if (n.endsWith('.svg')) return 'image/svg+xml';
  return 'application/octet-stream';
}

/**
 * If `downloadUrl` points at a locked-down `media-library/` object, copy it to
 * `portal-branding/` and return the new download URL. Otherwise returns the input.
 */
export async function ensurePortalPublicBrandingStorageUrl(
  downloadUrl: string,
  originalFileName: string
): Promise<string> {
  const path = firebaseStorageObjectPathFromDownloadUrl(downloadUrl);
  if (!path || path.startsWith('portal-branding/')) {
    return downloadUrl;
  }
  if (!path.startsWith('media-library/')) {
    return downloadUrl;
  }
  const safe = originalFileName.replace(/[^a-zA-Z0-9.-]/g, '_').slice(0, 120) || 'branding';
  const destPath = `portal-branding/${Date.now()}-${safe}`;
  const sourceRef = ref(storage, path);
  const destRef = ref(storage, destPath);
  const bytes = await getBytes(sourceRef);
  await uploadBytes(destRef, bytes, {
    contentType: contentTypeForFileName(originalFileName || path),
  });
  return getDownloadURL(destRef);
}

export async function ensurePortalPublicBrandingMediaItem<T extends { url: string; name: string }>(
  item: T | null
): Promise<T | null> {
  if (!item?.url?.trim()) return null;
  const url = await ensurePortalPublicBrandingStorageUrl(item.url, item.name);
  if (url === item.url) return item;
  return { ...item, url };
}
